import {useRouter} from 'next/router';
import {MouseEvent, SyntheticEvent, useCallback} from 'react';
import {t} from '../../common/getTranslations';
import {requestContext} from '../../common/requestContext';
import {useIsDarkMode} from '../../common/useIsDarkMode.hook';
import useTypedSelector from '../../common/useTypedSelector';
import {useDialog} from '../../dialog/useDialog.hook';
import {useContentHomeLikeAndAlarm} from '../../domain/content/[seriesId]/hook/useContentHomeLikeAndAlarm';
import {SaleMethod} from '../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../logging/logEvent';
import {putTiaraObjectProps} from '../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../reactQuery/constant';
import {useLogin} from '../../user/useLogin.hook';
import {useContentHomeOverviewQuery} from '../../wrapper/contentWrapper';
import {MobileContentHomeHeader} from '../organisms/MobileContentHomeHeader/MobileContentHomeHeader';

interface MobileLayoutContentHomeHeaderProps {
  onClickBack: (e: SyntheticEvent) => void;
  onClickHome: (e: SyntheticEvent) => void;
}

export function MobileLayoutContentHomeHeader({
  onClickBack,
  onClickHome,
}: MobileLayoutContentHomeHeaderProps) {
  const router = useRouter();
  const seriesId = Number(router.query.seriesId);
  const {
    isLikeOn,
    isAlarmOn,
    onClickLikeSeries,
    onClickAlarmOnSeries,
    showArea,
  } = useContentHomeLikeAndAlarm(seriesId);
  const isDarkMode = useIsDarkMode();
  const isCashTypeHome =
    useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
      ?.contentHomeOverview?.content?.saleMethod === SaleMethod.Cash;

  const isHeaderIconStatic = useTypedSelector(
    state => state.contentHome.isHeaderIconStatic
  );

  const {DialogWrapper, Dialog, hideDialog, showDialog} = useDialog({
    isMobile: requestContext.requestInfo.display.isMobile,
  });
  const onClickContentHomeMore = useCallback(() => {
    showDialog();
  }, [showDialog]);

  const {isLogin, doLogin} = useLogin();
  const handleClickHistory = useCallback(
    (e: MouseEvent) => {
      const actionName = isCashTypeHome
        ? '더보기팝업_콘텐츠구매내역_클릭'
        : '더보기팝업_이용권내역_클릭';

      LOG_CLICK_EVENT(actionName, e, () => {
        if (!isLogin) {
          doLogin();
          return;
        }

        router.push(
          `/content/${seriesId}/history/${isCashTypeHome ? 'buy' : 'ticket'}`
        );
      });
    },
    [doLogin, isLogin, router, isCashTypeHome, seriesId]
  );

  return (
    <div className="w-full">
      <MobileContentHomeHeader
        showLikeAndAlarmArea={showArea}
        isLikeActive={isLikeOn}
        isAlarmActive={isAlarmOn}
        isDarkMode={isDarkMode}
        onClickAlarm={onClickAlarmOnSeries}
        onClickLike={onClickLikeSeries}
        onClickMore={onClickContentHomeMore}
        onClickHome={onClickHome}
        onClickBack={onClickBack}
        seriesId={seriesId}
        isHeaderIconStatic={isHeaderIconStatic}
      />
      <DialogWrapper
        isUsingScrollBlock={false}
        {...putTiaraObjectProps({
          click: {layer1: '더보기'},
          customProps: {popup_type: '더보기팝업'},
        })}
      >
        <Dialog.Dimmed />
        <Dialog.List>
          <Dialog.Option onClick={handleClickHistory}>
            {isCashTypeHome ? '콘텐츠 구매 내역' : '이용권 내역'}
          </Dialog.Option>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button
            onClick={e =>
              LOG_CLICK_EVENT('더보기팝업_취소_클릭', e, hideDialog)
            }
          >
            {t('common_cancel_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </div>
  );
}
